import React from "react";
import { Modal, Typography, Divider, Button, Box } from "@mui/material";

interface ScriptGuidelinesModalProps {
  open: boolean;
  onClose: () => void;
  scriptType?: "text" | "speech";
}

const ScriptGuidelinesModal: React.FC<ScriptGuidelinesModalProps> = ({
  open,
  onClose,
  scriptType = "text",
}) => {
  const renderTextToSpeechGuidelines = () => (
    <Typography variant="body1" id="script-guidelines-description">
      <strong>Incorporate Punctuation Marks</strong>
      <ul>
        <li>
          <strong>Hyphens (-):</strong> Separate syllables for clear pronunciation.
        </li>
        <li>
          <strong>Commas (,):</strong> Create shorter breaks.
        </li>
        <li>
          <strong>Periods (.):</strong> Introduce longer breaks with downward inflection.
        </li>
        <li>
          <strong>Ellipsis (...):</strong> Create natural pause without inflection.
        </li>
        <li>
          <strong>Double Ellipsis (... …):</strong> Create a longer pause in between sections.
        </li>
      </ul>
      <strong>Ensure Correct Spelling and Language Consistency</strong>
      <ul>
        <li>
          <strong>Spell Correctly:</strong> Double-check your script for spelling errors.
        </li>
        <li>
          <strong>Language Consistency:</strong> Avoid mixing languages. For instance, do not
          include Arabic words in an English script.
        </li>
      </ul>
      <strong>Write Numbers Out for Easy Reading</strong>
      <ul>
        <li>You can write out numbers or use phonetic spelling for clarity. For example:</li>
        <ul>
          <li>
            <strong>"2012"</strong> becomes <strong>"twenty twelve."</strong>
          </li>
          <li>
            <strong>"3/8"</strong> becomes <strong>"three eighths of an inch."</strong>
          </li>
          <li>
            <strong>"01:18"</strong> becomes <strong>"one minute and eighteen seconds."</strong>
          </li>
          <li>
            <strong>"10-19-2016"</strong> becomes{" "}
            <strong>"October nineteenth, two thousand sixteen."</strong>
          </li>
          <li>
            <strong>"150th CT NE, Redmond, WA"</strong> becomes{" "}
            <strong>"150th Court Northeast, Redmond, Washington."</strong>
          </li>
        </ul>
      </ul>
    </Typography>
  );

  const renderSpeechToSpeechGuidelines = () => (
    <Typography variant="body1" id="script-guidelines-description">
      <strong>What is Speech-to-Speech?</strong>
      <p style={{ marginTop: "8px", marginBottom: "16px" }}>
        Speech-to-Speech allows you to upload an audio file and have your avatar speak the same
        content using a different voice. This is perfect for voice conversion, dubbing, or creating
        content in your avatar's voice from existing recordings.
      </p>

      <strong>How to Use Speech-to-Speech</strong>
      <ol style={{ marginTop: "8px", marginBottom: "16px" }}>
        <li>Upload your audio file using the audio upload section.</li>
        <li>Select an actor for your video.</li>
        <li>
          Open <strong>Voice Settings</strong> for your actor.
        </li>
        <li>
          Choose an <strong>ElevenLabs voice</strong> to convert your audio to that voice.
        </li>
        <li>
          Generate your video - the avatar will speak the content from your audio using the selected
          ElevenLabs voice.
        </li>
      </ol>

      <strong>Important Notes</strong>
      <ul>
        <li>
          <strong>ElevenLabs Voices:</strong> When you select an ElevenLabs voice, your uploaded
          audio will be converted to that voice. The avatar will speak the same words from your
          audio, but with the ElevenLabs voice you selected.
        </li>
        <li>
          <strong>Voice Library (Heygen) Voices:</strong> If you select a voice from the Voice
          Library instead of ElevenLabs, the avatar will lip-sync to your original audio exactly as
          uploaded - same words, same voice, no conversion.
        </li>
        <li>
          <strong>No Voice Selected:</strong> If you don't select any voice, the avatar will use
          your original audio exactly as uploaded.
        </li>
      </ul>

      <strong>Best Practices</strong>
      <ul>
        <li>Use clear, high-quality audio recordings for best results.</li>
        <li>Avoid background noise in your uploaded audio.</li>
        <li>For voice conversion, select an ElevenLabs voice that matches the tone you want.</li>
        <li>Preview the ElevenLabs voice before generating to ensure it fits your content.</li>
      </ul>
    </Typography>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="script-guidelines-modal"
      aria-describedby="script-guidelines-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxHeight: "80vh",
          overflow: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          margin: "10px",
        }}
      >
        <Typography variant="h6" id="script-guidelines-modal" gutterBottom>
          {scriptType === "speech"
            ? "How to Use Speech-to-Speech"
            : "Let's Launch Pronunciation Guidelines"}
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />
        {scriptType === "speech"
          ? renderSpeechToSpeechGuidelines()
          : renderTextToSpeechGuidelines()}
        <Button variant="contained" onClick={onClose} sx={{ marginTop: 2 }}>
          Close
        </Button>
      </Box>
    </Modal>
  );
};

export default ScriptGuidelinesModal;
